// ABOUTME: Custom hook for infinite scroll functionality with scroll detection and debouncing
// ABOUTME: Triggers loading when user scrolls within approximately 3 items from bottom
import { RefObject, useCallback, useEffect, useRef } from 'react';

type IUseInfiniteScrollProps = {
    readonly containerRef: RefObject<HTMLElement>;
    readonly onLoadMore: () => void;
    readonly hasMore: boolean;
    readonly isLoading: boolean;
    readonly threshold?: number; // Percentage of scroll height to trigger loading (default: 0.85 = 85%)
    readonly debounceMs?: number; // Debounce delay in milliseconds (default: 100)
};

type IUseInfiniteScrollReturn = {
    readonly isNearBottom: boolean;
    readonly triggerLoad: () => void;
};

export const useInfiniteScroll = ({
    containerRef,
    onLoadMore,
    hasMore,
    isLoading,
    threshold = 0.85,
    debounceMs = 100,
}: IUseInfiniteScrollProps): IUseInfiniteScrollReturn => {
    const isNearBottomRef = useRef(false);
    const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
    const isLoadingRef = useRef(isLoading);

    // Update loading ref when isLoading changes
    useEffect(() => {
        isLoadingRef.current = isLoading;
    }, [isLoading]);

    const checkScrollPosition = useCallback(() => {
        const container = containerRef.current;
        if (!container) return;

        const { scrollTop, scrollHeight, clientHeight } = container;

        // Skip if container is too small (might cause issues)
        if (scrollHeight <= clientHeight) return;

        // Calculate if we're near the bottom (default: 85% of scroll height)
        const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;
        const nearBottom = scrollPercentage >= threshold;

        isNearBottomRef.current = nearBottom;

        // Trigger loading if conditions are met
        if (nearBottom && hasMore && !isLoadingRef.current) {
            onLoadMore();
        }
    }, [containerRef, threshold, hasMore, onLoadMore]);

    const debouncedCheckScrollPosition = useCallback(() => {
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        debounceTimerRef.current = setTimeout(() => {
            checkScrollPosition();
        }, debounceMs);
    }, [checkScrollPosition, debounceMs]);

    const triggerLoad = useCallback(() => {
        if (hasMore && !isLoadingRef.current) {
            onLoadMore();
        }
    }, [hasMore, onLoadMore]);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        // Add scroll event listener with passive option for better performance
        container.addEventListener('scroll', debouncedCheckScrollPosition, { passive: true });

        // Initial check in case content is already at bottom
        checkScrollPosition();

        // Cleanup function
        // eslint-disable-next-line consistent-return
        return () => {
            container.removeEventListener('scroll', debouncedCheckScrollPosition);
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, [debouncedCheckScrollPosition, checkScrollPosition]);

    // Cleanup on unmount
    useEffect(
        () => () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        },
        [],
    );

    return {
        isNearBottom: isNearBottomRef.current,
        triggerLoad,
    };
};
